import { useCallback } from "react";
import useSWR from "swr";

import { deliveriesServices } from "../../../../../helpers/services/DeliveriesServices";
import { ResponseStatusEnum } from "../../../../../helpers/GlobalEnum";

/**
 * Extrae la ruta de un archivo por su índice (pdf, fe, evidence_1, evidence_2).
 */
const pick = (arr, key) =>
    arr.find((x) => String(x?.indice || "").toLowerCase() === key)?.ruta || null;

/**
 * Consulta la lista de entregas de un titular (CUB) con el detalle embebido.
 * GET /api/entrega/cub/2.0/{cubId}/
 */
const fetchDeliveriesList = async (cubId) => {
    const { data, status } = await deliveriesServices.searchDeliveriesToUserV2(cubId);
    if (status !== ResponseStatusEnum.OK || !Array.isArray(data)) return [];
    return data;
};

/**
 * Normaliza el detalle de una entrega al shape que consumen las columnas de la tabla.
 */
const mapDeliveryDetail = (data) => {
    const archivos = Array.isArray(data?.archivos) ? data.archivos : [];
    const reportError = data?.error || null;

    return {
        consolidatedFileUrl: pick(archivos, "pdf"),
        feFileUrl: pick(archivos, "fe"),
        evidence1Url: pick(archivos, "evidence_1"),
        evidence2Url: pick(archivos, "evidence_2"),
        approvedTechnical: data?.aprobado_tecnica ?? null,
        aprobadoProveedor: data?.aprobado_proveedor ?? null,
        fe_number: data?.numero_fe ?? null,
        statusDelivery: data?.estado ?? null,
        observation: data?.observacion ?? "",
        reportData: reportError ? null : data,
        reportError: reportError,
        reportProductosAlterados: data?.productos_alterados ?? [],
    };
};

/**
 * Consulta el detalle de una entrega.
 * GET /api/entrega/{deliveryId}/
 */
const fetchDeliveryDetail = async (deliveryId) => {
    try {
        const { data, status } = await deliveriesServices.searchDeliveriesPDF(deliveryId);
        if (status === ResponseStatusEnum.OK) return mapDeliveryDetail(data);
        return {
            reportData: null,
            reportError: data?.error || "Error obteniendo el reporte.",
            reportProductosAlterados: data?.productos_alterados ?? [],
        };
    } catch (error) {
        console.error("Error obteniendo el detalle de la entrega:", error);
        return {
            reportData: null,
            reportError: "Error obteniendo el reporte.",
            reportProductosAlterados: [],
        };
    }
};

/** Campos base de la fila (provienen de la lista). */
const baseFields = (row) => ({
    id: row?.id,
    date: row?.fecha_creacion ? row.fecha_creacion.split("T")[0] : "",
    // En el endpoint 2.0 `proveedor` es un objeto {id, nombre, nit, ...};
    // la columna PROVEEDOR pinta texto, así que extraemos el nombre.
    supplier:
        row?.proveedor && typeof row.proveedor === "object"
            ? row.proveedor.nombre ?? ""
            : row?.proveedor ?? "",
});

/** Campos de la fila que provienen del detalle de la entrega. */
const detailFields = (detail) => ({
    fe_number: detail?.fe_number,
    statusDelivery: detail?.statusDelivery ?? "",
    evidencePdf: {
        consolidatedFileUrl: detail?.consolidatedFileUrl ?? "",
        feFileUrl: detail?.feFileUrl ?? "",
    },
    evidenceImg: {
        imgEvidence1Url: detail?.evidence1Url ?? "",
        imgEvidence2Url: detail?.evidence2Url ?? "",
    },
    actions: {
        approvedTechnical: detail?.approvedTechnical,
        aprobadoProveedor: detail?.aprobadoProveedor,
    },
    observation: detail?.observation,
    reportData: detail?.reportData ?? null,
    reportError: detail?.reportError ?? null,
    reportProductosAlterados: detail?.reportProductosAlterados ?? [],
});

/** Combina la fila base con su detalle en el shape que consume la tabla. */
const mergeRow = (row, detail) => ({
    ...baseFields(row),
    ...detailFields(detail),
});

/**
 * Fetcher SWR: trae la lista de entregas con el detalle ya embebido en cada
 * elemento y devuelve las filas combinadas (mismo shape que consumía la tabla),
 * sin la cascada N+1 (una sola llamada al endpoint 2.0).
 */
const fetchDeliveriesWithDetails = async (cubId) => {
    const list = await fetchDeliveriesList(cubId);
    return list.map((row) => mergeRow(row, mapDeliveryDetail(row)));
};

/**
 * Orquesta la carga de las entregas de un titular vía SWR:
 * - Una sola fuente de datos cacheada (lista + detalle) por CUB.
 * - `refreshRow(id)` revalida SOLO una entrega tras una escritura puntual
 *   (aprobar / subir archivo / FE) sin recargar la página ni repetir todo el listado.
 * - `revalidate()` recarga la lista completa (crear / eliminar entregas).
 *
 * @param {number|string} cubId - ID del titular (CUB).
 */
export function useDeliveriesData(cubId) {
    const {
        data: rows = [],
        isLoading,
        mutate,
    } = useSWR(
        cubId ? ["deliveries-list", cubId] : null,
        () => fetchDeliveriesWithDetails(cubId),
        { revalidateOnFocus: false, dedupingInterval: 5000, keepPreviousData: true }
    );

    // Revalida el detalle de UNA sola entrega y lo aplica en caché sin recargar la lista.
    const refreshRow = useCallback(
        async (id) => {
            if (id == null) return;
            const detail = await fetchDeliveryDetail(id);
            await mutate(
                (current = []) =>
                    current.map((row) =>
                        row?.id === id ? { ...row, ...detailFields(detail) } : row
                    ),
                { revalidate: false }
            );
        },
        [mutate]
    );

    return {
        rows,
        isLoading,
        refreshRow,
        revalidate: mutate,
    };
}